import React, { useEffect } from "react"
import type { PaymentMethod } from "@mallardbay/lib-react-components"
import {
    Box,
    componentStyles,
    PaymentMethodSelector,
    TextLarge,
} from "@mallardbay/lib-react-components"

import type { PaymentMethodState } from "~utils/use-payment-method-state"
import {
    getCheckPaymentMethod,
    getDefaultPaymentMethod,
    isPaymentMethodAch,
    PAY_WITH_CHECK_VALUE,
} from "~utils/payment-method-helpers"
import COPY from "~config/copy-constants"

interface Props {
    readonly selectedId: string | null
    readonly onChange: (update: Partial<PaymentMethodState>) => void
    readonly paymentMethods: PaymentMethod[]
    readonly isExternalPaymentMethod?: boolean
    readonly isPayWithCheckEnabled?: boolean
}

export default function PaymentMethodSelectFormCore({
    selectedId,
    onChange,
    paymentMethods,
    isExternalPaymentMethod = false,
    isPayWithCheckEnabled = false,
}: Props) {
    const styles = useStyles()

    const selectedPaymentMethodId = isExternalPaymentMethod
        ? PAY_WITH_CHECK_VALUE
        : selectedId

    const allPaymentMethods = useAllPaymentMethods({
        paymentMethods,
        isPayWithCheckEnabled,
    })

    useEffect(() => {
        if (selectedPaymentMethodId) return

        const defaultPaymentMethod = getDefaultPaymentMethod(allPaymentMethods)

        if (!defaultPaymentMethod) return

        handleOnChange({
            selectedId: defaultPaymentMethod.id,
            paymentMethods: allPaymentMethods,
            onChange,
        })
    }, [allPaymentMethods, selectedPaymentMethodId])

    if (!allPaymentMethods.length) {
        return (
            <TextLarge style={styles.bottomMargin}>
                {COPY.NO_PAYMENT_METHODS}
            </TextLarge>
        )
    }

    return (
        <Box style={styles.bottomMargin}>
            <PaymentMethodSelector
                selectedPaymentMethodId={selectedPaymentMethodId}
                paymentMethods={allPaymentMethods}
                onChange={(paymentMethodId) =>
                    handleOnChange({
                        selectedId: paymentMethodId,
                        paymentMethods: allPaymentMethods,
                        onChange,
                    })
                }
            />
        </Box>
    )
}

function useStyles() {
    return componentStyles({
        bottomMargin: { marginBottom: 4 },
    })
}

function useAllPaymentMethods({
    paymentMethods,
    isPayWithCheckEnabled,
}: {
    paymentMethods: PaymentMethod[]
    isPayWithCheckEnabled?: boolean
}): PaymentMethod[] {
    const allPaymentBlocks: PaymentMethod[] = [...paymentMethods]

    if (isPayWithCheckEnabled) {
        allPaymentBlocks.push(getCheckPaymentMethod())
    }

    return allPaymentBlocks
}

function handleOnChange({
    selectedId,
    paymentMethods,
    onChange,
}: {
    selectedId: string
    paymentMethods: PaymentMethod[]
    onChange: (update: Partial<PaymentMethodState>) => void
}) {
    const selectedPaymentMethod = paymentMethods.find(
        ({ id }) => id === selectedId
    )
    const isAchPaymentMethod = isPaymentMethodAch(selectedPaymentMethod)
    const isExternalPaymentMethod = selectedId === PAY_WITH_CHECK_VALUE
    const paymentMethodId = isExternalPaymentMethod ? null : selectedId

    onChange({ paymentMethodId, isAchPaymentMethod, isExternalPaymentMethod })
}
